/* @skip-file-for-translation */
import { t } from '@lingui/core/macro';

import {
  WatershedPlan,
  WatershedPlanLegacy,
} from '@watershed/constants/organization';
import assertNever from '@watershed/shared-util/src/assertNever';

export const WATERSHED_PLAN_ORDER: ReadonlyArray<WatershedPlan> = [
  WatershedPlan.Premium,
  WatershedPlan.Enterprise,
  WatershedPlan.Standard,
  WatershedPlan.Essential,
];

export const WATERSHED_PLAN_LEGACY_ORDER: ReadonlyArray<WatershedPlanLegacy> = [
  WatershedPlanLegacy.NetZero,
  WatershedPlanLegacy.Pro,
  WatershedPlanLegacy.Standard,
  WatershedPlanLegacy.Lite,
  WatershedPlanLegacy.NoPlan,
];

export function tryToMapNewPlanToLegacyPlan(
  watershedPlan: WatershedPlan
): WatershedPlanLegacy | null {
  switch (watershedPlan) {
    case WatershedPlan.Essential:
      return WatershedPlanLegacy.Lite;
    case WatershedPlan.Standard:
      return WatershedPlanLegacy.Standard;
    case WatershedPlan.Enterprise:
      return WatershedPlanLegacy.Pro;
    case WatershedPlan.Premium:
      return WatershedPlanLegacy.NetZero;
    default:
      return null;
  }
}

export function isWatershedPlanAtLeastWithFallback({
  watershedPlan,
  watershedPlanAtLeast,
  watershedPlanLegacy,
  watershedPlanLegacyAtLeast,
}: {
  watershedPlan: WatershedPlan;
  watershedPlanAtLeast: WatershedPlan;
  /**
   * In the case of `Custom` and `Other` Watershed Plan, optional fallback to a
   * legacy plan for gating.
   */
  watershedPlanLegacy: WatershedPlanLegacy;
  watershedPlanLegacyAtLeast?: WatershedPlanLegacy;
}): { hasAtLeast: boolean; usedFallback: boolean } {
  if (
    watershedPlanLegacyAtLeast &&
    (
      [
        WatershedPlan.Custom,
        WatershedPlan.Other,
        WatershedPlan.None,
        WatershedPlan.Finance,
      ] as ReadonlyArray<WatershedPlan>
    ).includes(watershedPlan)
  ) {
    if (
      isWatershedPlanAtLeast(
        watershedPlanLegacy,
        watershedPlanLegacyAtLeast,
        WATERSHED_PLAN_LEGACY_ORDER
      )
    ) {
      return { hasAtLeast: true, usedFallback: true };
    }
    return { hasAtLeast: false, usedFallback: true };
  }
  if (
    isWatershedPlanAtLeast(
      watershedPlan,
      watershedPlanAtLeast,
      WATERSHED_PLAN_ORDER
    )
  ) {
    return { hasAtLeast: true, usedFallback: false };
  }
  return { hasAtLeast: false, usedFallback: false };
}

function isWatershedPlanAtLeast<T extends WatershedPlan | WatershedPlanLegacy>(
  orgPlan: T,
  atLeast: T,
  order: ReadonlyArray<T>
): boolean {
  const orgPlanIndex = order.indexOf(orgPlan);
  const atLeastIndex = order.indexOf(atLeast);
  if (orgPlanIndex === -1 || orgPlanIndex > atLeastIndex) {
    return false;
  }
  return true;
}

export function getWatershedPlanLegacyName(plan: WatershedPlanLegacy): string {
  switch (plan) {
    case WatershedPlanLegacy.Lite:
      return t`Lite`;
    case WatershedPlanLegacy.Pro:
      return t`Pro`;
    case WatershedPlanLegacy.Standard:
      return t`Standard`;
    case WatershedPlanLegacy.NetZero:
      return t`Enterprise`;
    case WatershedPlanLegacy.NoPlan:
      return t`No Plan`;
    default:
      assertNever(plan);
  }
}

/**
 * "Super legacy" means N - 2 notion of what "Watershed Plan" means.
 * E.g. "Lite" and "Pro" are the original or "super legacy" plans.
 * And anything else in `WatershedPlanLegacy` is "legacy".
 */
export function isSuperLegacyWatershedPlan(plan: WatershedPlanLegacy): boolean {
  switch (plan) {
    case WatershedPlanLegacy.Lite:
    case WatershedPlanLegacy.Pro:
      return true;
    case WatershedPlanLegacy.Standard:
    case WatershedPlanLegacy.NetZero:
    case WatershedPlanLegacy.NoPlan:
      return false;
    default:
      assertNever(plan);
  }
}

export function watershedPlanIncludesReductions({
  watershedPlan,
  watershedPlanLegacy,
  overrideFlag,
}: {
  watershedPlan: WatershedPlan;
  watershedPlanLegacy: WatershedPlanLegacy;
  // The PricingFy25ReductionsModuleTemp flag overrides the WS plan to enable
  // reductions. This is a temporary flag until we can get feature overrides
  // from Salesforce.
  overrideFlag: boolean | undefined;
}): boolean {
  if (overrideFlag) {
    return true;
  }
  const { hasAtLeast } = isWatershedPlanAtLeastWithFallback({
    watershedPlan,
    watershedPlanAtLeast: WatershedPlan.Enterprise,
    watershedPlanLegacy,
    // Don't check reductions access against the legacy plan
  });
  return hasAtLeast;
}

export function watershedPlanIncludesMeasurement({
  watershedPlan,
  watershedPlanLegacy,
}: {
  watershedPlan: WatershedPlan;
  watershedPlanLegacy: WatershedPlanLegacy;
}): boolean {
  const { hasAtLeast } = isWatershedPlanAtLeastWithFallback({
    watershedPlan,
    watershedPlanAtLeast: WatershedPlan.Essential,
    watershedPlanLegacy,
    watershedPlanLegacyAtLeast: WatershedPlanLegacy.Lite,
  });
  return hasAtLeast;
}

export function watershedPlanIncludesTcfdQualitative({
  watershedPlan,
  watershedPlanLegacy,
}: {
  watershedPlan: WatershedPlan;
  watershedPlanLegacy: WatershedPlanLegacy;
}): boolean {
  const { hasAtLeast } = isWatershedPlanAtLeastWithFallback({
    watershedPlan,
    watershedPlanAtLeast: WatershedPlan.Enterprise,
    watershedPlanLegacy,
    watershedPlanLegacyAtLeast: WatershedPlanLegacy.Pro,
  });
  return hasAtLeast;
}
